"use client";

import { useTheme } from "next-themes";
import { use, useCallback, useId, useMemo } from "react";
import { useMounted } from "@/hooks/use-mounted";

function Mermaid({ chart }: { chart: string }) {
  const mounted = useMounted();

  if (!mounted) {
    return null;
  }
  return <MermaidContent chart={chart} />;
}

let mermaidPromise: Promise<typeof import("mermaid")> | null = null;

function getMermaidPromise() {
  if (!mermaidPromise) {
    mermaidPromise = import("mermaid");
  }
  return mermaidPromise;
}

const renderCache = new Map<
  string,
  Promise<{ svg: string; bindFunctions?: (element: Element) => void }>
>();

function MermaidContent({ chart }: { chart: string }) {
  const id = useId();
  const { resolvedTheme } = useTheme();

  const { default: mermaid } = use(getMermaidPromise());

  useMemo(() => {
    mermaid.initialize({
      fontFamily: "inherit",
      securityLevel: "loose",
      startOnLoad: false,
      theme: resolvedTheme === "dark" ? "dark" : "default",
      themeCSS: "margin: 1.5rem auto 0;",
    });
  }, [mermaid, resolvedTheme]);

  const renderPromise = useMemo(() => {
    const cacheKey = `${chart}-${resolvedTheme}`;
    const cached = renderCache.get(cacheKey);
    if (cached) {
      return cached;
    }

    const promise = mermaid.render(id, chart.replaceAll("\\n", "\n"));
    renderCache.set(cacheKey, promise);
    return promise;
  }, [chart, resolvedTheme, id, mermaid]);

  const { svg, bindFunctions } = use(renderPromise);
  const bindDiagram = useCallback(
    (container: HTMLDivElement | null) => {
      if (container) {
        bindFunctions?.(container);
      }
    },
    [bindFunctions]
  );

  return (
    <div
      // biome-ignore lint/security/noDangerouslySetInnerHtml: <!-- Mermaid renders SVG content -->
      dangerouslySetInnerHTML={{ __html: svg }}
      ref={bindDiagram}
    />
  );
}

export { Mermaid };
